<template>
	<n-spin :show="loading">
		<ElementsTransition type="fade-up" :duration="250">
			<!-- Options -->
			<div v-if="!model.otpMethod" key="options" class="flex flex-col gap-8">
				<div class="flex items-start justify-between gap-5">
					<div class="flex flex-col gap-3">
						<div class="font-medium" :class="size === 'small' ? 'text-fs-lg' : 'text-fs-xl'">
							{{ $t("auth.mfa.choose_method_title") }}
						</div>
						<div class="text-secondary">
							{{ $t("auth.mfa.choose_method_description") }}
						</div>
					</div>

					<slot name="header-extra" />
				</div>

				<div class="flex flex-col gap-4">
					<!-- TOTP not available banner -->
					<n-alert v-if="!availableMethods.includes('TOTP') && !loadingMethods" type="info">
						<template #icon>
							<Icon name="carbon:information" />
						</template>
						<span v-html="$t('auth.mfa.totp_not_available_message')"></span>
					</n-alert>

					<!-- TOTP available card -->
					<Card
						bordered
						:class="{
							'hover:border-primary! transition-colors duration-300': availableMethods.includes('TOTP')
						}"
						:content-class="`flex items-center gap-4 ${availableMethods.includes('TOTP') ? 'cursor-pointer' : 'opacity-40'}`"
						@click="requestOtp('TOTP')"
					>
						<Icon name="carbon:two-factor-authentication" :size="32" />
						<div class="flex grow flex-col gap-0.5">
							<div class="text-primary text-xs font-bold">{{ $t("auth.mfa.recommended_choice") }}</div>
							<div class="text-lg font-bold">
								{{ $t("auth.mfa.authenticator_app") }}
							</div>
							<div class="text-secondary text-sm">
								{{ $t("auth.mfa.authenticator_app_description") }}
							</div>
						</div>
						<Icon name="carbon:chevron-right" :size="24" />
					</Card>

					<!-- EMAIL card -->
					<Card
						v-if="availableMethods.includes('EMAIL')"
						bordered
						:class="{
							'hover:border-primary! transition-colors duration-300': availableMethods.includes('EMAIL')
						}"
						content-class="flex cursor-pointer items-center gap-4"
						@click="requestOtp('EMAIL')"
					>
						<Icon name="carbon:email" :size="32" />
						<div class="flex grow flex-col gap-0.5">
							<div class="text-lg font-bold">{{ $t("auth.mfa.email") }}</div>
							<div class="text-secondary text-sm">
								{{ $t("auth.mfa.email_description") }}
							</div>
						</div>
						<Icon name="carbon:chevron-right" :size="24" />
					</Card>

					<!-- SMS card -->
					<Card
						v-if="availableMethods.includes('SMS')"
						bordered
						:class="{
							'hover:border-primary! transition-colors duration-300': availableMethods.includes('SMS')
						}"
						content-class="flex cursor-pointer items-center gap-4"
						@click="requestOtp('SMS')"
					>
						<Icon name="carbon:send" :size="32" />
						<div class="flex grow flex-col gap-0.5">
							<div class="text-lg font-bold">{{ $t("auth.mfa.sms") }}</div>
							<div class="text-secondary text-sm">{{ $t("auth.mfa.sms_description") }}</div>
						</div>
						<Icon name="carbon:chevron-right" :size="24" />
					</Card>
				</div>

				<n-checkbox
					v-if="showRememberDevice"
					v-model:checked="model.rememberDevice"
					size="large"
					:disabled="loading"
				>
					{{ $t(`auth.otp.remember_device`) }}
				</n-checkbox>
			</div>

			<!-- TOTP -->
			<div v-else-if="model.otpMethod === 'TOTP'" key="totp" class="flex flex-col gap-8 p-1">
				<JMButton circle type="secondary" :disabled @click="model.otpMethod = null">
					<template #icon>
						<Icon name="carbon:arrow-left" />
					</template>
				</JMButton>

				<div class="flex flex-col gap-3">
					<div class="font-medium" :class="size === 'small' ? 'text-fs-lg' : 'text-fs-xl'">
						{{ $t("auth.mfa.enter_otp_title") }}
					</div>
					<div class="text-secondary">
						{{ $t("auth.mfa.enter_otp_totp_description") }}
					</div>
				</div>

				<OtpInput
					:ref="el => otpInputRefs.push(el as InstanceType<typeof OtpInput>)"
					v-model:value="model.otp"
					:disabled="loading || disabled"
					size="extra-large"
					autofocus
					fluid
					class="w-full"
					@completed="validate()"
					@validation="isOtpValid = $event"
				/>

				<JMButton
					type="primary"
					size="large"
					:disabled="!isValid || disabled"
					:label="action === 'LOGIN' ? $t(`auth.login.login_button`) : $t(`auth.mfa.validate_button`)"
					@click="validate()"
				/>
			</div>

			<!-- EMAIL -->
			<div v-else-if="model.otpMethod === 'EMAIL'" key="email" class="flex flex-col gap-8 p-1">
				<JMButton circle type="secondary" :disabled @click="model.otpMethod = null">
					<template #icon>
						<Icon name="carbon:arrow-left" />
					</template>
				</JMButton>

				<div class="flex flex-col gap-3">
					<div class="font-medium" :class="size === 'small' ? 'text-fs-lg' : 'text-fs-xl'">
						{{ $t("auth.mfa.email_sent_title") }}
					</div>
					<div
						class="text-secondary"
						v-html="$t('auth.mfa.email_sent_description', { email: props.email || '' })"
					></div>
				</div>

				<OtpInput
					:ref="el => otpInputRefs.push(el as InstanceType<typeof OtpInput>)"
					v-model:value="model.otp"
					:disabled="loading || disabled"
					size="extra-large"
					autofocus
					fluid
					class="w-full"
					@completed="validate()"
					@validation="isOtpValid = $event"
				/>

				<div class="flex w-full flex-wrap gap-2">
					<span>{{ $t("auth.otp.code_not_received") }}</span>
					<JMButton text type="primary" size="large" @click="requestOtp('EMAIL')">
						{{ $t("auth.otp.resend") }}
					</JMButton>
				</div>

				<JMButton
					type="primary"
					size="large"
					:disabled="!isValid || disabled"
					:label="action === 'LOGIN' ? $t(`auth.login.login_button`) : $t(`auth.mfa.validate_button`)"
					@click="validate()"
				/>
			</div>

			<!-- SMS -->
			<div v-else-if="model.otpMethod === 'SMS'" key="sms" class="flex flex-col gap-8 p-1">
				<JMButton circle type="secondary" :disabled @click="model.otpMethod = null">
					<template #icon>
						<Icon name="carbon:arrow-left" />
					</template>
				</JMButton>

				<div class="flex flex-col gap-3">
					<div class="font-medium" :class="size === 'small' ? 'text-fs-lg' : 'text-fs-xl'">
						{{ $t("auth.mfa.sms_sent_title") }}
					</div>
					<div class="text-secondary">
						{{ $t("auth.mfa.sms_sent_description") }}
					</div>
				</div>

				<OtpInput
					:ref="el => otpInputRefs.push(el as InstanceType<typeof OtpInput>)"
					v-model:value="model.otp"
					:disabled="loading || disabled"
					size="extra-large"
					fluid
					autofocus
					class="w-full"
					@completed="validate()"
					@validation="isOtpValid = $event"
				/>

				<div class="flex w-full flex-wrap gap-2">
					<span>{{ $t("auth.otp.code_not_received") }}</span>
					<JMButton text type="primary" size="large" @click="requestOtp('SMS')">
						{{ $t("auth.otp.resend") }}
					</JMButton>
				</div>

				<JMButton
					type="primary"
					size="large"
					:disabled="!isValid || disabled"
					:label="action === 'LOGIN' ? $t(`auth.login.login_button`) : $t(`auth.mfa.validate_button`)"
					@click="validate()"
				/>
			</div>
		</ElementsTransition>
	</n-spin>
</template>

<script setup lang="ts">
import type { OtpRequestPayload } from "@/api/endpoints/common/auth"
import type { I18nString } from "@/lang/config"
import type { OtpRequestMethods, OtpValidateMethods } from "@/types/auth"
import { NAlert, NCheckbox, NSpin, useMessage } from "naive-ui"
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import Api from "@/api"
import Card from "@/components/common/Card.vue"
import ElementsTransition from "@/components/common/ElementsTransition.vue"
import Icon from "@/components/common/Icon.vue"
import OtpInput from "@/components/common/input/OtpInput.vue"
import JMButton from "@/components/common/JMButton.vue"
import { getApiErrorMessage } from "@/utils"

export interface MfaModel {
	otp: string | null
	otpMethod: OtpValidateMethods | null
	rememberDevice: boolean
}

const props = defineProps<{
	methods?: OtpValidateMethods[] | null
	email?: string | null
	showRememberDevice?: boolean
	action: "LOGIN" | "GENERIC_AUTH"
	size?: "small"
	disabled?: boolean
}>()

const emit = defineEmits<{
	(e: "validate", value: MfaModel): void
	(e: "error", error: Error): void
}>()

const { t } = useI18n()
const message = useMessage()

const model = ref<MfaModel>({
	otp: null,
	otpMethod: null,
	rememberDevice: false
})

const otpInputRefs = ref<InstanceType<typeof OtpInput>[]>([])
const isOtpValid = ref(false)
const availableMethods = ref<OtpValidateMethods[]>([])
const loadingMethods = ref<boolean>(false)
const loadingRequest = ref<boolean>(false)

const loading = computed(() => loadingMethods.value || loadingRequest.value)

const isValid = computed(() => {
	return isOtpValid.value
})

function resetOtpValue() {
	model.value.otp = null
	otpInputRefs.value.forEach(el => {
		el.focusOnChar()
	})
}

// Se methods non viene passato, carica i metodi automaticamente
function getOtpMethods() {
	loadingMethods.value = true

	Api.common.auth
		.getOtpMethods()
		.then(res => {
			availableMethods.value = res.data
		})
		.catch(err => {
			emit("error", err as Error)
			message.error(getApiErrorMessage(err) || t<I18nString>(`common.misc.generic_error`))
		})
		.finally(() => {
			loadingMethods.value = false
		})
}

// Inizializza i metodi: usa quelli passati come prop o caricali automaticamente
watch(
	() => props.methods,
	methods => {
		if (methods !== null && methods !== undefined) {
			availableMethods.value = methods
		} else if (availableMethods.value.length === 0 && !loadingMethods.value) {
			getOtpMethods()
		}
	},
	{ immediate: true }
)

function requestOtp(method: OtpValidateMethods) {
	if (!availableMethods.value.includes(method)) {
		return
	}

	model.value.otpMethod = method

	if (method === "TOTP") {
		return
	}

	loadingRequest.value = true

	const payload: OtpRequestPayload = {
		method: method as OtpRequestMethods,
		source: props.email || "",
		action: props.action
	}

	Api.common.auth
		.requestOtp(payload)
		.then(() => {
			message.success(t<I18nString>(`auth.otp.code_sent`))
		})
		.catch(err => {
			emit("error", err as Error)
			message.error(getApiErrorMessage(err) || t<I18nString>(`form.validators.verification_code_error`))
		})
		.finally(() => {
			loadingRequest.value = false
		})
}

function validate() {
	emit("validate", model.value)
}

defineExpose({
	resetOtpValue
})
</script>
